const capitalize = (str) => {
  if (typeof str !== "string") throw new Error("The input must be a string");
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const toTitleCase = (str, separator) => {
  return str
    .split(separator || " ")
    .map(capitalize)
    .join(" ");
};

const toSnakeCase = (str, separator) => {
  return str
    .split(separator || " ")
    .join("_")
    .toLowerCase();
};

/**
 * Build an ISO 8601 string with timezone offset from a Date object and offset string
 * @param {Date} date - JavaScript Date object
 * @param {string} offsetStr - Offset string like "+05:30" or "-07:00"
 * @param {string} [zoneId] - Optional zone name for logging (e.g., "Asia/Kolkata")
 * @returns {string} ISO string with custom offset (e.g., 2025-05-08T14:30:00+05:30)
 */
function buildIsoStringWithOffsetStr(date, timeZone) {
  const pad = (n) => String(n).padStart(2, '0');
  // Extract date parts from the Date object
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // JS months are 0-based
  const day = date.getDate();
  const hour = date.getHours();
  const minute = date.getMinutes();
  const second = 0;

  const timeZoneObj = splitTimezoneAndOffset(timeZone)

  // Validate offsetStr format
  const offsetRegex = /^[+-](0\d|1[0-4]):([0-5]\d)$/;
  if (!offsetRegex.test(timeZoneObj.offset)) {
    throw new Error(`Invalid offset format: ${timeZoneObj.offset}`);
  }

  // Build ISO string manually
  const isoString =
    `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}${timeZoneObj.offset}[${timeZoneObj.zoneId}]`;
  return isoString;
}

/**
 * Accepts millisecond and timezone values 
 * @param {*} millis 
 * @param {*} timeZone 
 * @returns date object in mentioned timeZone
 */
const getDateInTimeZone = (millis, timeZone) => {

  if(isNumber(millis) == false) {
    return millis;
  }

  const date = new Date(millis);

  const timeZoneObj = splitTimezoneAndOffset(timeZone);

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timeZoneObj.zoneId,
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short", 
  });

  return formatter.format(date).replace(/\b(am|pm)\b/, (match) => match.toUpperCase());;
}

/**
 * Accepts timeZone in (GMT +05:30) Asia/Kolkata formate and 
 * @returns timeZoneId and offSet as array. ['Asia/Kolkata', '+05:30']
 * @param {*} timezone 
 */
const splitTimezoneAndOffset = (timezone) => {
  const match = new String(timezone).match(/\(GMT ([+\-]\d{2}:\d{2})\)\s(.+)/)
  return {zoneId : match[2], offset : match[1]}
}

const isNumber = (str) => { 
  // parse as number
  const num = Number(str);
  if (!Number.isFinite(num)) return false;

  // Java long range is ±9,223,372,036,854,775,807
  const MAX_SAFE_LONG = 9223372036854775807;
  const MIN_SAFE_LONG = -9223372036854775808;

  // NOTE: Number can't safely represent values beyond ±2^53-1
  return num >= MIN_SAFE_LONG && num <= MAX_SAFE_LONG;
}

const toCamelCase = (str, separator) => {
  return str
    .split(separator || " ")
    .map((word, index) => {
      if (index === 0) return word.toLowerCase();
      return capitalize(word);
    })
    .join();
};

const toKebabCase = (str, separator) => {
  return str
    .split(separator || " ")
    .join("-")
    .toLowerCase();
};

const truncate = (str, length) => {
  return str.length > length ? str.slice(0, length) + "..." : str;
};

const slugify = (str, separator) => {
  return str.toLowerCase().replace(separator || /\s/g, "-");
};

const isValidEmail = (email) => {
  if(!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}

const setHourMinuteFromTimeString = (date, timeString) => {
  // Example timeString: "18 May 2025, 10:30 AM UTC"
  
  const parts = timeString.split(",")[1].trim().split(" "); // ["10:30", "AM", "UTC"]
  const [hourStr, minuteStr] = parts[0].split(":");
  const ampm = parts[1].toUpperCase();

  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  if (ampm === "PM" && hour !== 12) hour += 12;
  if (ampm === "AM" && hour === 12) hour = 0;

  // Set time on the Date object
  date.setHours(hour);
  date.setMinutes(minute);
  date.setSeconds(0);

  return date;
}

const getLocalTimeZoneOffsetString = () => {
  const tz = getCanonicalTimeZone();
  const now = new Date();

  // Offset in minutes from UTC
  const offsetMinutes = -now.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";

  const absMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, "0");
  const minutes = String(absMinutes % 60).padStart(2, "0");

  const result = `(GMT ${sign}${hours}:${minutes}) ${tz}`
  return result;
}

function getCanonicalTimeZone() {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  try {
    const canonicalList = Intl.supportedValuesOf('timeZone');
    const canonical = canonicalList.find(tz => tz.toLowerCase() === timeZone.toLowerCase());
    return canonical || timeZone; // fallback to original if not found
  } catch (e) {
    return timeZone; // older JS environments won't support supportedValuesOf
  }
}

function isValidUNCPath(uncPath) {
  const UNC_PATH_REGEX = /^\\\\(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?|\d{1,3}(?:\.\d{1,3}){3})\\[a-zA-Z0-9$._-]+(?:\\[a-zA-Z0-9._-]+)*$/;
  return UNC_PATH_REGEX.test(uncPath.trim());
}

export {
  capitalize,
  toTitleCase,
  truncate,
  slugify,
  toSnakeCase,
  toCamelCase,
  toKebabCase,
  isValidEmail,
  buildIsoStringWithOffsetStr,
  getDateInTimeZone,
  setHourMinuteFromTimeString,
  getLocalTimeZoneOffsetString,
  isValidUNCPath,
};
